import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  prefix?: ReactNode;
}

// Строгое текстовое поле в стиле лендинга: label, мелкой uppercase-подписью,
// сам инпут, с 1px-рамкой `line`, фокус, `ink`. Никаких теней и закруглений.
export default forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, hint, error, prefix, className, id, ...rest },
  ref,
) {
  const inputId = id || `fld-${rest.name || label}`;
  return (
    <label htmlFor={inputId} className="block">
      <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-ink/55">
        {label}
      </span>
      <div
        className={clsx(
          'mt-2 flex items-center border bg-white transition-colors',
          error ? 'border-orange' : 'border-line focus-within:border-ink',
        )}
      >
        {prefix && (
          <div className="pl-4 pr-3 text-sm text-ink/60 border-r border-line self-stretch flex items-center">
            {prefix}
          </div>
        )}
        <input
          id={inputId}
          ref={ref}
          {...rest}
          className={clsx(
            'w-full bg-transparent px-4 py-3.5 text-[15px] text-ink placeholder:text-ink/35 focus:outline-none',
            className,
          )}
        />
      </div>
      {(hint || error) && (
        <span
          className={clsx(
            'mt-1.5 block text-[12px] leading-snug',
            error ? 'text-orange' : 'text-ink/55',
          )}
        >
          {error || hint}
        </span>
      )}
    </label>
  );
});
